import React, { useCallback, useState } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { ReactComponent as RevolutWordmark } from './revolut-wordmark.svg';
import { useTranslation } from '@shared/core';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import { pxToRem } from '@withjoy/joykit/theme';
import { AllyAmountDisplay, AllyAmountSubtext, AllyPromoHeadline, AllyPromoSubtext, AllyPrimaryButton, AllySecondaryButton, AllyFinePrint } from './TransferToBankDialog.styles';
import type { TransferPartnerConfig } from './transferPartnerConfigs';

export type RevolutTransferPromoProps = Readonly<{
  config: TransferPartnerConfig;
  standardAmountFormatted: string;
  /** No-bank state: triggers connect-bank flow. Pending state: dismisses promo and falls through to transfer confirm. */
  onSkip?: () => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

export const RevolutTransferPromo: React.FC<RevolutTransferPromoProps> = ({ config, standardAmountFormatted, onSkip, onBankPromoInteracted }) => {
  const { tq } = useTranslation('transferToBankDialog');
  const openWindow = useWindowFunction('open');
  const [partnerOpened, setPartnerOpened] = useState(false);

  const handleOpenAccount = useCallback(() => {
    onBankPromoInteracted?.('open_account', config.partnerId);
    openWindow(config.ctaUrl, '_blank', 'noopener,noreferrer');
    setPartnerOpened(true);
  }, [onBankPromoInteracted, openWindow, config.partnerId, config.ctaUrl]);

  const handleSkip = useCallback(() => {
    onBankPromoInteracted?.('skip', config.partnerId);
    onSkip?.();
  }, [onBankPromoInteracted, onSkip, config.partnerId]);

  const handleContinue = useCallback(() => {
    onBankPromoInteracted?.('continue', config.partnerId);
    onSkip?.();
  }, [onBankPromoInteracted, onSkip, config.partnerId]);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      paddingX={7}
      paddingBottom={6}
    >
      {!partnerOpened && (
        <>
          <AllyAmountDisplay style={{ color: '#111111' }}>{tq`Earn ${config.apyRate}`}</AllyAmountDisplay>
          <AllyAmountSubtext marginBottom={2}>{tq`APY¹ (variable) on your cash gifts`}</AllyAmountSubtext>
          <RevolutWordmark
            style={{ height: pxToRem(22), width: 'auto', display: 'block', marginTop: pxToRem(16), marginBottom: pxToRem(20) }}
            aria-label="Revolut"
          />
          <AllyPromoHeadline marginBottom={3}>{tq`Your wedding gifts, earning ${config.apyRate} APY¹ — paid monthly, access anytime.`}</AllyPromoHeadline>
          <Box
            width="100%"
            height="1px"
            backgroundColor="#eeeeee"
            marginY={5}
          />
        </>
      )}

      <AllyPromoSubtext marginBottom={1}>{tq`Cash gifts received`}</AllyPromoSubtext>
      <AllyAmountDisplay
        style={{ color: '#111111' }}
        marginBottom={pxToRem(40)}
      >
        {standardAmountFormatted}
      </AllyAmountDisplay>

      {partnerOpened ? (
        <AllyPrimaryButton
          type="button"
          size="lg"
          shape="rounded"
          intent="neutral"
          onClick={handleContinue}
        >
          {tq`Continue`}
        </AllyPrimaryButton>
      ) : (
        <>
          <AllyPrimaryButton
            type="button"
            size="lg"
            shape="rounded"
            intent="neutral"
            onClick={handleOpenAccount}
          >
            {tq`Open ${config.partnerDisplayName} Account`}
          </AllyPrimaryButton>
          <AllySecondaryButton
            type="button"
            size="lg"
            shape="rounded"
            variant="outline"
            intent="neutral"
            onClick={handleSkip}
          >
            {tq`Transfer to another account instead`}
          </AllySecondaryButton>
          {config.partnerId === 'revolut' && (
            <AllyFinePrint>
              {tq`¹ Revolut is not a bank. Banking services are provided by Lead Bank, Member FDIC. Fees may apply. The Annual Percentage Yield (APY) is a variable rate and may change at any time. APYs vary by plan and account type. See revolut.com/en-US/ for details.`}
            </AllyFinePrint>
          )}
        </>
      )}
    </Flex>
  );
};
